import { ContentRecord, ContentQuery } from '../stores/contentStore';
import { 
  AdminSegmentDto, 
  AdminAssessmentResponse, 
  AdminAssessmentState, 
  AdminQuestionResponse,
  AdminNodeDto,
  AdminNodeOrderDto,
  AdminNodeParamsDto,
  AdminNodeMeanDto,
  AdminNodeProbDto,
  AdminNodeDProbDto,
  AdminNodeCutsDto
} from '../types/adminSegment';
import { StorageInterface } from '../stores/childNavStores';
import { toMemoryOptimized } from '../utils/tableAccess';
import { 
  MasterMilestone, 
  MilestoneText, 
  MasterMilestoneData 
} from '../types/milestones';
import { createMasterMilestoneStorage } from '../storage/masterMilestoneStorage';
import {
  StoredConcernData,
  StoredConcernGroup,
  StoredConcernItem,
  CONCERN_STORAGE_KEY,
  deriveConcernDomain
} from '../types/concerns';

// Debug logging utility
const isDevLoggingEnabled = (): boolean => {
  // Only enable verbose logging when explicitly requested via ADMIN_SEGMENT_DEBUG env var
  return (globalThis as any).process?.env?.ADMIN_SEGMENT_DEBUG === 'true';
};

/**
 * AdminSegment Storage Service - Handles unpacking and accessing structured AdminSegment data
 */
export class AdminSegmentStorage {
  private storage: StorageInterface;
  private cache: Map<string, AdminSegmentDto> = new Map();

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  /**
   * Unpack AdminSegment from ContentRecord and cache it
   */
  async unpackAdminSegment(contentRecord: ContentRecord): Promise<AdminSegmentDto | null> {
    try {      
      const cacheKey = `${contentRecord.contentType}_${contentRecord.contentTypeId}_${contentRecord.languageTypeId}`;
      
      // Check cache first
      if (this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey)!;
      }

      // Get content body from TableData
      const tableData = contentRecord.tableData;
      
      if (!tableData?.Data?.Rows || tableData.Data.Rows.length === 0) {
        return null;
      }
      
      // Find content_body column
      const bodyColumnIndex = tableData.Data.Columns.findIndex(col => 
        col.toLowerCase() === 'content_body'
      );
      
      if (bodyColumnIndex === -1) {
        return null;
      }
      
      // Get the base64 encoded content body from first row
      const base64Content = tableData.Data.Rows[0][bodyColumnIndex] as string;
      if (!base64Content) {
        return null;
      }
      

      // Decode base64 and parse JSON
      const jsonString = this.decodeBase64(base64Content);
      const adminSegmentDto = JSON.parse(jsonString) as AdminSegmentDto;
            
      // 🐛 DIAGNOSTIC: always log DTO structure so we can see what arrived
      const dtoKeys = Object.keys(adminSegmentDto);
      const pagesVal = (adminSegmentDto as any).Pages ?? (adminSegmentDto as any).pages;
      const nodesVal = (adminSegmentDto as any).Nodes ?? (adminSegmentDto as any).nodes;

      // Validate the structure
      if (!this.validateAdminSegmentDto(adminSegmentDto)) {
        return null;
      }
      
      // Store AdminNodeDto information by domain name
      await this.storeAdminNodesByDomain(adminSegmentDto, contentRecord.contentTypeId);

      // Cache the parsed result
      this.cache.set(cacheKey, adminSegmentDto);
      
      return adminSegmentDto;

    } catch (error) {
      console.error('Failed to unpack AdminSegment:', error);
      return null;
    }
  }

  /**
   * Get AdminSegment by assessment ID and language
   */
  async getAdminSegment(
    assessmentId: string, 
    languageId: string,
    contentService: any
  ): Promise<AdminSegmentDto | null> {
    try {
      const cacheKey = `AdminSegment_${assessmentId}_${languageId}`;
      
      // Check cache first
      if (this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey)!;
      }

      // Get content from contentService (checks local cache first, then fetches from backend if needed)
      const tableData = await contentService.getContent(
        'AdminSegment',
        assessmentId,
        languageId
      );
      
      if (!tableData) {
        return null;
      }
      

      // Create ContentRecord from TableData for unpacking
      // Note: AdminSegment tableData uses {Data:{Columns,Rows}} format, not the {Schema,PrimaryKey,Data:{Rows}}
      // format that QuickAccessTable requires. unpackAdminSegment only uses tableData.Data.Columns/Rows
      // so quickAccess is intentionally skipped here to avoid a runtime crash.
      const contentRecord = {
        id: `AdminSegment_${assessmentId}_${languageId}`,
        contentType: 'AdminSegment',
        contentTypeId: assessmentId,
        languageTypeId: languageId,
        tableData: tableData,
        quickAccess: (tableData as any)?.Schema ? toMemoryOptimized(tableData) : null as any,
        lastUpdated: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        syncVersion: '1.0',
        isOutdated: false
      };
      
      return await this.unpackAdminSegment(contentRecord);

    } catch (error) {
      console.error('Failed to get AdminSegment:', error);
      return null;
    }
  }

  /**
   * Get page by page name
   */
  getPageByName(adminSegment: AdminSegmentDto, pageName: string) {
    return adminSegment.Pages.find(page => page.Name === pageName);
  }

  /**
   * Get page by page number string
   */
  getPageByNumber(adminSegment: AdminSegmentDto, pageNumber: string) {
    return adminSegment.Pages.find(page => page.PageNum === pageNumber);
  }

  /**
   * Get all pages sorted by page number
   */
  getSortedPages(adminSegment: AdminSegmentDto) {
    return [...adminSegment.Pages].sort((a, b) => {
      const aNum = parseInt(a.PageNum) || 0;
      const bNum = parseInt(b.PageNum) || 0;
      return aNum - bNum;
    });
  }

  /**
   * Get question by variable name
   */
  getQuestionByMvarName(adminSegment: AdminSegmentDto, mvarName: string) {
    for (const page of adminSegment.Pages) {
      for (const questionList of page.Questions) {
        if (questionList.Question?.MvarName === mvarName) {
          return questionList.Question;
        }
      }
    }
    return null;
  }

  /**
   * Save assessment response
   */
  async saveAssessmentResponse(response: AdminAssessmentResponse): Promise<void> {
    try {
      const key = `assessment_response_${response.assessmentId}_${response.sessionId}`;
      await this.storage.setItem(key, JSON.stringify(response));
    } catch (error) {
      console.error('Failed to save assessment response:', error);
    }
  }

  /**
   * Load assessment response
   */
  async loadAssessmentResponse(assessmentId: string, sessionId: string): Promise<AdminAssessmentResponse | null> {
    try {
      const key = `assessment_response_${assessmentId}_${sessionId}`;
      const responseJson = await this.storage.getItem(key);
      if (responseJson) {
        const response = JSON.parse(responseJson) as AdminAssessmentResponse;
        // Convert responses array back to Map for easier access
        return response;
      }
    } catch (error) {
      console.error('Failed to load assessment response:', error);
    }
    return null;
  }

  /**
   * Save assessment state
   */
  async saveAssessmentState(assessmentId: string, sessionId: string, state: AdminAssessmentState): Promise<void> {
    try {
      const key = `assessment_state_${assessmentId}_${sessionId}`;
      // Convert Map to Object for JSON serialization
      const serializable = {
        ...state,
        responses: Array.from(state.responses.entries())
      };
      await this.storage.setItem(key, JSON.stringify(serializable));
    } catch (error) {
      console.error('Failed to save assessment state:', error);
    }
  }

  /**
   * Load assessment state
   */
  async loadAssessmentState(assessmentId: string, sessionId: string): Promise<AdminAssessmentState | null> {
    try {
      const key = `assessment_state_${assessmentId}_${sessionId}`;
      const stateJson = await this.storage.getItem(key);
      if (stateJson) {
        const serialized = JSON.parse(stateJson);
        // Convert responses array back to Map
        return {
          ...serialized,
          responses: new Map(serialized.responses)
        };
      }
    } catch (error) {
      console.error('Failed to load assessment state:', error);
    }
    return null;
  }

  /**
   * Clear cache entry
   */
  clearCache(assessmentId?: string, languageId?: string): void {
    if (assessmentId && languageId) {
      const cacheKey = `AdminSegment_${assessmentId}_${languageId}`;
      this.cache.delete(cacheKey);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Decode base64 content
   */
  private decodeBase64(base64String: string): string {
    try {
      // Handle both Node.js and browser environments
      const NodeBuffer = typeof (globalThis as any).Buffer !== 'undefined' ? (globalThis as any).Buffer : undefined;
      if (NodeBuffer) {
        // Node.js / React Native environment
        return NodeBuffer.from(base64String, 'base64').toString('utf-8');
      } else {
        // Browser environment
        return atob(base64String);
      }
    } catch (error) {
      console.error('Failed to decode base64 content:', error);
      throw new Error('Invalid base64 content');
    }
  }

  /**
   * Check if AdminSegment uses PAS (Probabilistic Assessment System) processing
   */
  isPasSegment(adminSegment: AdminSegmentDto): boolean {
    const segType = String(adminSegment.SegmentType || '');
    return (segType === 'PAS' || segType === '5') && Array.isArray(adminSegment.Nodes) && adminSegment.Nodes.length > 0;
  }

  /**
   * Get node by name for PAS processing
   */
  getNodeByName(adminSegment: AdminSegmentDto, nodeName: string) {
    if (!this.isPasSegment(adminSegment)) {
      return null;
    }
    return adminSegment.Nodes.find(node => node.Name === nodeName);
  }

  /**
   * Get all available nodes for PAS processing
   */
  getAllNodes(adminSegment: AdminSegmentDto) {
    if (!this.isPasSegment(adminSegment)) {
      return [];
    }
    return adminSegment.Nodes;
  }

  /**
   * Get node parameter by name
   */
  getNodeParameter(node: any, paramName: string): string | null {
    if (!node?.Params || !Array.isArray(node.Params)) {
      return null;
    }
    const param = node.Params.find((p: any) => p.Name === paramName);
    return param?.Val || null;
  }

  /**
   * Get node order configuration
   */
  getNodeOrder(node: any): any[] {
    return node?.Order || [];
  }

  /**
   * Get node probability configuration
   */
  getNodeProbs(node: any): any[] {
    return node?.Probs || [];
  }

  /**
   * Get node cuts configuration
   */
  getNodeCuts(node: any): any[] {
    return node?.Cuts || [];
  }

  /**
   * Process PAS node based on current state and responses
   */
  processPasNode(adminSegment: AdminSegmentDto, nodeName: string, responses: Map<string, any>): any {
    const node = this.getNodeByName(adminSegment, nodeName);
    if (!node) {
      return null;
    }

    // Basic PAS processing - this can be extended based on specific requirements
    const result = {
      nodeName: node.Name,
      parameters: node.Params,
      order: node.Order,
      probs: node.Probs,
      cuts: node.Cuts,
      pages: node.Pages,
      processedAt: new Date().toISOString()
    };

    return result;
  }

  /**
   * Evaluate PAS probabilities based on responses
   */
  evaluatePasProbs(node: any, responses: Map<string, any>): { [key: string]: number } {
    const probResults: { [key: string]: number } = {};
    
    if (!node?.Probs || !Array.isArray(node.Probs)) {
      return probResults;
    }

    for (const prob of node.Probs) {
      if (prob.Name && prob.DProbs && Array.isArray(prob.DProbs)) {
        let totalProb = 0;
        
        for (const dProb of prob.DProbs) {
          const responseValue = responses.get(dProb.DVal);
          if (responseValue !== undefined) {
            totalProb += parseFloat(dProb.Prob) || 0;
          }
        }
        
        probResults[prob.Name] = totalProb;
      }
    }

    return probResults;
  }

  /**
   * Apply PAS cuts based on probability results
   */
  applyPasCuts(node: any, probResults: { [key: string]: number }): { [key: string]: boolean } {
    const cutResults: { [key: string]: boolean } = {};
    
    if (!node?.Cuts || !Array.isArray(node.Cuts)) {
      return cutResults;
    }

    for (const cut of node.Cuts) {
      if (cut.RVal && cut.CVal) {
        const threshold = parseFloat(cut.CVal) || 0;
        const probValue = probResults[cut.RVal] || 0;
        cutResults[cut.RVal] = probValue >= threshold;
      }
    }

    return cutResults;
  }

  /**
   * Milestone domain mapping configuration
   */
  private static readonly MILESTONE_NODE_MAPPING = {
    standard: {
      segmentId: '11255',
      nodes: {
        communication: 'CDC_LC',
        motor: 'CDC_MT',
        cognitive: 'CDC_CG',
        socialEmotional: 'CDC_SE'
      }
    },
    premium: {
      segmentId: '11270',
      nodes: {
        communication: 'CDCX_LC',
        grossMotor: 'CDCX_GM',
        fineMotor: 'CDCX_FM',
        cognitive: 'CDCX_CG',
        socialEmotional: 'CDCX_SE'
      }
    }
  };

  /**
   * Extract milestones from AdminSegment nodes based on domain mapping
   */
  async extractMilestonesFromNodes(
    contentService: any,
    languageId: string = '1',
    milestoneType: 'standard' | 'premium' = 'standard'
  ): Promise<{ milestones: any[], milestoneTexts: any[], domainPasData: { [domain: string]: any } }> {
    
    const mapping = AdminSegmentStorage.MILESTONE_NODE_MAPPING[milestoneType];
    const segmentId = mapping.segmentId;
    
    // Load AdminSegment containing milestone nodes
    const adminSegment = await this.getAdminSegment(segmentId, languageId, contentService);
    
    if (!adminSegment) {
      return { milestones: [], milestoneTexts: [], domainPasData: {} };
    }
    
    
    if (!this.isPasSegment(adminSegment)) {
      return { milestones: [], milestoneTexts: [], domainPasData: {} };
    }
    
    const milestones: any[] = [];
    const milestoneTexts: any[] = [];
        
    // Process each domain mapping
    for (const [domainKey, nodeId] of Object.entries(mapping.nodes)) {
      
      // Find AdminNodeDto by node name
      const node = adminSegment.Nodes.find(n => n.Name === nodeId);
      if (!node) {
        continue;
      }
      
      // Extract milestones from AdminPageDto pages within the node
      if (!node.Pages || node.Pages.length === 0) {
         continue;
      }

      
      // Create order lookup map for this domain node
      const orderLookup = new Map<string, number>();
      if (node.Order && Array.isArray(node.Order)) {
        node.Order.forEach((orderItem: any, orderIndex: number) => {
           
          if (orderItem.Name && orderItem.Val !== undefined && orderItem.Val !== null) {
            // Handle different data types for Val field
            let parsedAge: number;
            
            if (typeof orderItem.Val === 'number') {
              parsedAge = orderItem.Val;
            } else if (typeof orderItem.Val === 'string') {
              // Try parseFloat first to handle decimal values, fallback to parseInt
              parsedAge = parseFloat(orderItem.Val) || parseInt(orderItem.Val) || 0;
            } else {
              console.warn(`    - Unexpected Val type for ${orderItem.Name}: ${typeof orderItem.Val}, using 0`);
              parsedAge = 0;
            }
            
            orderLookup.set(orderItem.Name, parsedAge);
          } else {
            console.warn(`    - Skipped order item ${orderIndex}: Missing Name or Val`);
          }
        });
      }
      
      // Process each page as a milestone
      node.Pages.forEach((page, pageIndex) => {
         
        if (!page.Questions || page.Questions.length === 0) {
          return;
        }

        // Process each question as a milestone
        page.Questions.forEach((questionListItem, questionIndex) => {
          
          if (!questionListItem.Question) {
            return;
          }

          const question = questionListItem.Question;
          
          const mvar = question.MvarName;
          const seqNum = (pageIndex * page.Questions.length) + questionIndex + 1;
          
          if (!mvar) {
             return;
          }

          // Extract milestone text from question HTML
          const milestoneText = this.extractQuestionText(question);
          
          if (!milestoneText) {
            return;
          }

          // Get item age from Order data if available, otherwise calculate
          const itemAge = orderLookup.get(mvar) || this.calculateItemAge(seqNum);
          const ageSource = orderLookup.has(mvar) ? 'Order data' : 'calculated';
 
          // Create milestone definition (display data only) - domain-agnostic
          const milestone = {
            mvar,
            itemage: itemAge,
            seqnum: seqNum,
            lastUpdated: new Date().toISOString(),
            version: 1
          };
          
          milestones.push(milestone);

          // Create milestone text
          const milestoneTextObj = {
            mvar,
            languagetype: 'en', // Default to English
            htmltext: milestoneText,
            lastUpdated: new Date().toISOString(),
            version: 1
          };
          
          milestoneTexts.push(milestoneTextObj);
        });
      });
    }
    
    // Sort milestones by itemage to maintain chronological order from AdminNodeOrderDto
    milestones.sort((a, b) => {
      // Sort by itemage (chronological order)
      return (a.itemage || 0) - (b.itemage || 0);
    });
    
    // Also sort milestone texts to match milestone order
    milestoneTexts.sort((a, b) => {
      const milestoneA = milestones.find(m => m.mvar === a.mvar);
      const milestoneB = milestones.find(m => m.mvar === b.mvar);
      if (!milestoneA || !milestoneB) return 0;
      
      // Sort by itemage
      return (milestoneA.itemage || 0) - (milestoneB.itemage || 0);
    });
        
    // Create domain-level PAS information for developmental age computation
    const domainPasData: { [domain: string]: any } = {};
    for (const [domainKey, nodeId] of Object.entries(mapping.nodes)) {
      const node = adminSegment.Nodes.find(n => n.Name === nodeId);
      if (node) {
        // Process Order array to create milestone ordering information
        const orderData = (node.Order || []).map((orderItem: any) => ({
          mvarName: orderItem.Name,     // Milestone variable name
          itemAge: orderItem.Val,       // Item age for the milestone
          orderIndex: orderItem.Index || 0  // Order index if available
        }));
        
        domainPasData[domainKey] = {
          nodeId,
          params: node.Params || [],
          means: node.Means || [],
          probs: node.Probs || [],
          cuts: node.Cuts || [],
          order: orderData,             // Milestone ordering information
          lastUpdated: new Date().toISOString()
        };
     }
    }
    
    return { milestones, milestoneTexts, domainPasData };
  }

  /**
   * Reload Standard milestones only, preserving Premium milestones
   */
  async reloadStandardMilestones(
    contentService: any,
    languageId: string = '1'
  ): Promise<void> {
    
    // Create milestone storage instance
    const milestoneStorage = createMasterMilestoneStorage(this.storage);
    
    try {
      // Load existing data to preserve Premium milestones
      let existingMilestones: MasterMilestone[] = [];
      let existingTexts: MilestoneText[] = [];
      let existingPasData: { [domain: string]: any } = {};
      
      try {
        const existingData = await milestoneStorage.loadMasterData();
        if (existingData) {
          existingMilestones = existingData.milestones || [];
          existingTexts = existingData.milestoneTexts || [];
          
          // Load existing PAS data
          const existingPasDataRaw = await this.getDomainPasData();
          if (existingPasDataRaw) {
            existingPasData = existingPasDataRaw;
            
            // Debug: Show what existing PAS data exists before merge
            console.log('🔍 Existing PAS data before Standard merge:');
            Object.keys(existingPasData).forEach(domain => {
              const orderCount = existingPasData[domain]?.order?.length || 0;
              console.log(`  - ${domain}: ${orderCount} order items`);
            });
          }
        }
      } catch (error) {
        console.warn('⚠️ No existing data found:', error);
      }
      
      // Load new Standard milestones
       const standardData = await this.extractMilestonesFromNodes(contentService, languageId, 'standard');
      
      // Merge milestones - update existing or add new ones
      const mergedMilestones: MasterMilestone[] = [...existingMilestones];
      const mergedTexts: MilestoneText[] = [...existingTexts];
      
      let updatedCount = 0;
      let addedCount = 0;
      
      // Process each new Standard milestone
      for (const newMilestone of standardData.milestones) {
        const existingIndex = mergedMilestones.findIndex(m => m.mvar === newMilestone.mvar);
        
        if (existingIndex >= 0) {
          // Update existing milestone with any newer data (but preserve domain-agnostic structure)
          mergedMilestones[existingIndex] = {
            ...mergedMilestones[existingIndex],
            ...newMilestone,
            lastUpdated: new Date().toISOString()
          };
          updatedCount++;
        } else {
          // Add new milestone
          mergedMilestones.push(newMilestone);
          addedCount++;
        }
      }
      
      // Process each new Standard milestone text
      for (const newText of standardData.milestoneTexts) {
        const existingTextIndex = mergedTexts.findIndex(t => t.mvar === newText.mvar);
        
        if (existingTextIndex >= 0) {
          // Update existing text
          mergedTexts[existingTextIndex] = {
            ...mergedTexts[existingTextIndex],
            ...newText,
            lastUpdated: new Date().toISOString()
          };
        } else {
          // Add new text
          mergedTexts.push(newText);
        }
      }
      
      // Merge PAS data (Standard domains replace existing Standard domains, preserve Premium)
      const combinedPasData = { ...existingPasData, ...standardData.domainPasData };
            
      // Save merged data
      const milestoneData: MasterMilestoneData = {
        milestones: mergedMilestones,
        milestoneTexts: mergedTexts,
        milestoneAnswers: [],
        milestoneAnswerTexts: [],
        concernAnswers: [],
        concernAnswerTexts: [],
        languages: [
          {
            code: 'en',
            name: 'English',
            isDefault: true,
            isSupported: true
          }
        ],
        lastSyncTimestamp: new Date().toISOString(),
        dataVersion: '1.0.0-standard-reload'
      };
      
      await milestoneStorage.saveMasterData(milestoneData);
      await this.storage.setItem('domain_pas_data', JSON.stringify(combinedPasData));

    } catch (error) {
      console.error('❌ Error reloading Standard milestones:', error);
      throw error;
    }
  }
  
  /**
   * Reload Premium milestones only, preserving Standard milestones  
   */
  async reloadPremiumMilestones(
    contentService: any,
    languageId: string = '1'
  ): Promise<void> {
    console.log('🔄 Reloading Premium milestones from AdminSegment nodes...');
    
    // Create milestone storage instance
    const milestoneStorage = createMasterMilestoneStorage(this.storage);
    
    try {
      // Load existing data to preserve Standard milestones
      let existingMilestones: MasterMilestone[] = [];
      let existingTexts: MilestoneText[] = [];
      let existingPasData: { [domain: string]: any } = {};
      
      try {
        const existingData = await milestoneStorage.loadMasterData();
        if (existingData) {
          existingMilestones = existingData.milestones || [];
          existingTexts = existingData.milestoneTexts || [];
          
          // Load existing PAS data
          const existingPasDataRaw = await this.getDomainPasData();
          if (existingPasDataRaw) {
            existingPasData = existingPasDataRaw;
          }
        }
      } catch (error) {
        console.warn('⚠️ No existing data found:', error);
      }
      
      // Load new Premium milestones
      const premiumData = await this.extractMilestonesFromNodes(contentService, languageId, 'premium');
      
      // Merge milestones - update existing or add new ones
      const mergedMilestones: MasterMilestone[] = [...existingMilestones];
      const mergedTexts: MilestoneText[] = [...existingTexts];
      
      let updatedCount = 0;
      let addedCount = 0;
      
      // Process each new Premium milestone
      for (const newMilestone of premiumData.milestones) {
        const existingIndex = mergedMilestones.findIndex(m => m.mvar === newMilestone.mvar);
        
        if (existingIndex >= 0) {
          // Update existing milestone with any newer data (but preserve domain-agnostic structure)
          mergedMilestones[existingIndex] = {
            ...mergedMilestones[existingIndex],
            ...newMilestone,
            lastUpdated: new Date().toISOString()
          };
          updatedCount++;
        } else {
          // Add new milestone
          mergedMilestones.push(newMilestone);
          addedCount++;
        }
      }
      
      // Process each new Premium milestone text
      for (const newText of premiumData.milestoneTexts) {
        const existingTextIndex = mergedTexts.findIndex(t => t.mvar === newText.mvar);
        
        if (existingTextIndex >= 0) {
          // Update existing text
          mergedTexts[existingTextIndex] = {
            ...mergedTexts[existingTextIndex],
            ...newText,
            lastUpdated: new Date().toISOString()
          };
        } else {
          // Add new text
          mergedTexts.push(newText);
        }
      }
      
      // Add premium prefix to domain PAS data to avoid conflicts with Standard domains
      const premiumPasData: { [domain: string]: any } = {};
      Object.entries(premiumData.domainPasData).forEach(([domain, pasData]) => {
        premiumPasData[`premium_${domain}`] = pasData;
      });
      
      // Merge PAS data (Premium domains with prefix, preserve existing Standard and other Premium)
      // Use explicit merging to ensure no Standard domains are overwritten
      const combinedPasData = { ...existingPasData };
      
      // Add Premium PAS data with prefixes (should not conflict with Standard domains)
      Object.keys(premiumPasData).forEach(premiumDomain => {
        combinedPasData[premiumDomain] = premiumPasData[premiumDomain];
      });
      
      // Verify Standard motor domain is preserved
      if (existingPasData.motor && !combinedPasData.motor) {
        console.error('❌ Standard motor domain PAS data was lost during merge!');
        // Restore it
        combinedPasData.motor = existingPasData.motor;
      } else if (combinedPasData.motor) {
        const motorOrderCount = combinedPasData.motor?.order?.length || 0;
      }
      
      // Save merged data
      const milestoneData: MasterMilestoneData = {
        milestones: mergedMilestones,
        milestoneTexts: mergedTexts,
        milestoneAnswers: [],
        milestoneAnswerTexts: [],
        concernAnswers: [],
        concernAnswerTexts: [],
        languages: [
          {
            code: 'en',
            name: 'English',
            isDefault: true,
            isSupported: true
          }
        ],
        lastSyncTimestamp: new Date().toISOString(),
        dataVersion: '1.0.0-premium-reload'
      };
      
      await milestoneStorage.saveMasterData(milestoneData);
      await this.storage.setItem('domain_pas_data', JSON.stringify(combinedPasData));
      
    } catch (error) {
      console.error('❌ Error reloading Premium milestones:', error);
      throw error;
    }
  }
  
  /**
   * Clear milestone storage and reload from AdminSegment nodes (legacy method - clears all)
   */
  async reloadMilestonesFromNodes(
    contentService: any,
    languageId: string = '1',
    includeStandard: boolean = true,
    includePremium: boolean = false
  ): Promise<void> {
    
    // Use targeted reload methods when possible to preserve data
    if (includeStandard && !includePremium) {
      return await this.reloadStandardMilestones(contentService, languageId);
    } else if (!includeStandard && includePremium) {
      return await this.reloadPremiumMilestones(contentService, languageId);
    }
    
    // Create milestone storage instance
    const milestoneStorage = createMasterMilestoneStorage(this.storage);
    
    // Clear existing milestone data (single source of truth: MASTER_DATA_KEY)    console.log('🗑️ Clearing existing milestone data from MASTER_DATA_KEY...');
    try {
      // Clear the single source of truth for milestone data
      await this.storage.removeItem('masterMilestones'); // This is MASTER_DATA_KEY
      await this.storage.removeItem('domain_pas_data');
      
      // Clean up any legacy data that might exist
      await this.storage.removeItem('milestoneTexts');
      await this.storage.removeItem('milestoneAnswers');
      await this.storage.removeItem('milestoneAnswerTexts');
      await this.storage.removeItem('concernAnswers');
      await this.storage.removeItem('concernAnswerTexts');
      await this.storage.removeItem('milestone_metadata');
      
      // Clear AdminSegment cache to free memory
      this.clearCache();
      
    } catch (error) {
      console.warn('⚠️ Error clearing storage:', error);
      // Continue anyway, might be first run
    }
    
    const allMilestones: MasterMilestone[] = [];
    const allMilestoneTexts: MilestoneText[] = [];
    let allDomainPasData: { [domain: string]: any } = {};

    // Load standard milestones if requested
    if (includeStandard) {
      const standardData = await this.extractMilestonesFromNodes(contentService, languageId, 'standard');
      
      // Add Standard milestones, handling duplicates by updating existing ones
      for (const newMilestone of standardData.milestones) {
        const existingIndex = allMilestones.findIndex(m => m.mvar === newMilestone.mvar);
        
        if (existingIndex >= 0) {
          // Update existing milestone
          allMilestones[existingIndex] = {
            ...allMilestones[existingIndex],
            ...newMilestone,
            lastUpdated: new Date().toISOString()
          };
        } else {
          // Add new milestone
          allMilestones.push(newMilestone);
        }
      }
      
      // Add Standard milestone texts, handling duplicates
      for (const newText of standardData.milestoneTexts) {
        const existingTextIndex = allMilestoneTexts.findIndex(t => t.mvar === newText.mvar);
        
        if (existingTextIndex >= 0) {
          // Update existing text
          allMilestoneTexts[existingTextIndex] = {
            ...allMilestoneTexts[existingTextIndex],
            ...newText,
            lastUpdated: new Date().toISOString()
          };
        } else {
          // Add new text
          allMilestoneTexts.push(newText);
        }
      }
      
      allDomainPasData = { ...allDomainPasData, ...standardData.domainPasData };
    }

    // Load premium milestones if requested
    if (includePremium) {
      const premiumData = await this.extractMilestonesFromNodes(contentService, languageId, 'premium');
      
      // Add Premium milestones, handling duplicates by updating existing ones
      for (const newMilestone of premiumData.milestones) {
        const existingIndex = allMilestones.findIndex(m => m.mvar === newMilestone.mvar);
        
        if (existingIndex >= 0) {
          // Update existing milestone with Premium data
          allMilestones[existingIndex] = {
            ...allMilestones[existingIndex],
            ...newMilestone,
            lastUpdated: new Date().toISOString()
          };
        } else {
          // Add new milestone
          allMilestones.push(newMilestone);
        }
      }
      
      // Add Premium milestone texts, handling duplicates
      for (const newText of premiumData.milestoneTexts) {
        const existingTextIndex = allMilestoneTexts.findIndex(t => t.mvar === newText.mvar);
        
        if (existingTextIndex >= 0) {
          // Update existing text
          allMilestoneTexts[existingTextIndex] = {
            ...allMilestoneTexts[existingTextIndex],
            ...newText,
            lastUpdated: new Date().toISOString()
          };
        } else {
          // Add new text
          allMilestoneTexts.push(newText);
        }
      }
      
      // Merge domain PAS data with premium prefix to avoid conflicts
      Object.entries(premiumData.domainPasData).forEach(([domain, pasData]) => {
        allDomainPasData[`premium_${domain}`] = pasData;
      });
    }

    // Create new milestone data structure with separated domain PAS data
    const milestoneData: MasterMilestoneData = {
      milestones: allMilestones,
      milestoneTexts: allMilestoneTexts,
      milestoneAnswers: [],
      milestoneAnswerTexts: [],
      concernAnswers: [],
      concernAnswerTexts: [],
      languages: [
        {
          code: 'en',
          name: 'English',
          isDefault: true,
          isSupported: true
        }
      ],
      lastSyncTimestamp: new Date().toISOString(),
      dataVersion: '1.0.0-separated-pas'
    };

    // Save milestone data directly to MASTER_DATA_KEY (single source of truth)
    try {      
      // Save to milestone storage using standard format
      await milestoneStorage.saveMasterData(milestoneData);
      
      // Save domain PAS data separately for developmental age computation
      await this.storage.setItem('domain_pas_data', JSON.stringify(allDomainPasData));
      
    } catch (error) {
      console.error('❌ Error saving milestone data:', error);
      
      // If storage is still full, try emergency cleanup and retry with essential data only
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage?.includes('full') || errorMessage?.includes('SQLITE_FULL')) {
        console.log('🚨 Storage full detected, attempting emergency cleanup...');
        await this.emergencyStorageCleanup();
        
        // Retry with essential data only
        const essentialMilestones = allMilestones.slice(0, 50); // Keep more milestones since no PAS data
        const essentialTexts = allMilestoneTexts.slice(0, 50);
        
        const minimalData: MasterMilestoneData = {
          milestones: essentialMilestones,
          milestoneTexts: essentialTexts,
          milestoneAnswers: [],
          milestoneAnswerTexts: [],
          concernAnswers: [],
          concernAnswerTexts: [],
          languages: [{ code: 'en', name: 'English', isDefault: true, isSupported: true }],
          lastSyncTimestamp: new Date().toISOString(),
          dataVersion: '1.0.0-adminsegment-essential'
        };
        
        await milestoneStorage.saveMasterData(minimalData);
        
        // Still save domain PAS data even in minimal mode
        try {
          await this.storage.setItem('domain_pas_data', JSON.stringify(allDomainPasData));
          console.log('✅ Domain PAS data saved in minimal mode');
        } catch (error) {
          console.warn('⚠️ Could not save domain PAS data in minimal mode');
        }
        
        console.log(`⚠️ Saved essential dataset: ${essentialMilestones.length} milestones due to storage constraints`);
      } else {
        throw error; // Re-throw if not storage related
      }
    }
  }

  /**
   * Extract milestone text from AdminQuestionDto
   */
  private extractQuestionText(question: any): string | null {
    
    // Try to extract text from HtmlQuestion array
    if (question.HtmlQuestion && Array.isArray(question.HtmlQuestion) && question.HtmlQuestion.length > 0) {
      const htmlQuestion = question.HtmlQuestion[0];
      
      if (htmlQuestion.QText) {
       // Strip HTML tags to get plain text
        const cleanText = htmlQuestion.QText.replace(/<[^>]*>/g, '').trim();
        return cleanText;
      }
    }
    
    // Fallback to other possible text fields
    if (question.questionText) {
      return question.questionText.trim();
    }
    
    if (question.text) {
      return question.text.trim();
    }
    
    return null;
  }

  /**
   * Calculate item age for milestone based on sequence number
   */
  private calculateItemAge(seqNum: number): number {
    // Standard age progression: start at 12 months, increment by 3 months
    return 12 + ((seqNum - 1) * 3);
  }

  /**
   * Get domain PAS data for developmental age computation
   */
  async getDomainPasData(): Promise<{ [domain: string]: any } | null> {
    try {
      const dataStr = await this.storage.getItem('domain_pas_data');
      return dataStr ? JSON.parse(dataStr) : null;
    } catch (error) {
      console.error('Error loading domain PAS data:', error);
      return null;
    }
  }

  /**
   * Get milestone ordering data for a specific domain
   */
  async getDomainOrderData(domain: string): Promise<Array<{ mvarName: string, itemAge: number, orderIndex: number }> | null> {
    try {
      const domainPasData = await this.getDomainPasData();
      if (domainPasData && domainPasData[domain] && domainPasData[domain].order) {
        return domainPasData[domain].order;
      }
      return null;
    } catch (error) {
      console.error('Error loading domain order data:', error);
      return null;
    }
  }

  /**
   * Get all domains with available order data
   */
  async getDomainsWithOrderData(): Promise<string[]> {
    try {
      const domainPasData = await this.getDomainPasData();
      if (!domainPasData) return [];
      
      return Object.keys(domainPasData).filter(domain => 
        domainPasData[domain].order && domainPasData[domain].order.length > 0
      );
    } catch (error) {
      console.error('Error loading domains with order data:', error);
      return [];
    }
  }

  /**
   * Emergency cleanup when storage is full
   */
  private async emergencyStorageCleanup(): Promise<void> {
    console.log('🚨 Emergency storage cleanup initiated...');
    
    try {
      // Clear all cached data
      this.clearCache();
      
      // Remove large data items that might be taking up space
      const keysToTry = [
        'masterMilestones', // This is MASTER_DATA_KEY - the single source of truth
        'milestoneTexts', 
        'milestoneAnswers',
        'milestoneAnswerTexts',
        'concernAnswers',
        'concernAnswerTexts',
        'domain_pas_data',
        'assessmentState',
        'assessmentResponse'
      ];
      
      for (const key of keysToTry) {
        try {
          await this.storage.removeItem(key);
          console.log(`  🗑️ Removed ${key}`);
        } catch (error) {
          // Continue with other keys if one fails
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.log(`  ⚠️ Could not remove ${key}:`, errorMessage);
        }
      }
      
      // Clean up any legacy chunked data that might exist from previous versions
      try {
        await this.storage.removeItem('milestone_metadata');
        for (let i = 0; i < 5; i++) {
          await this.storage.removeItem(`milestones_chunk_${i}`);
          await this.storage.removeItem(`milestoneTexts_chunk_${i}`);
        }
        console.log('  🗑️ Cleaned up legacy chunked data');
      } catch (error) {
        // No legacy chunks to clean up
      }
      
      console.log('✅ Emergency cleanup completed');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Emergency cleanup failed:', errorMessage);
    }
  }

  /**
   * Validate AdminSegmentDto structure (updated for PAS support)
   */
  private validateAdminSegmentDto(dto: any): dto is AdminSegmentDto {
    
    if (!dto) {
      console.log('      ❌ dto is null/undefined');
      return false;
    }
    
    if (typeof dto.SurveybId !== 'string') {
      console.log('      ❌ SurveybId is not string:', typeof dto.SurveybId, dto.SurveybId);
      return false;
    }
    
    if (typeof dto.Title !== 'string') {
      console.log('      ❌ Title is not string:', typeof dto.Title, dto.Title);
      return false;
    }
    
    if (typeof dto.LanguageTypeId !== 'string') {
      console.log('      ❌ LanguageTypeId is not string:', typeof dto.LanguageTypeId, dto.LanguageTypeId);
      return false;
    }
    
    if (typeof dto.SegmentType !== 'string') {
      console.log('      ❌ SegmentType is not string:', typeof dto.SegmentType, dto.SegmentType);
      return false;
    }
    
    if (!Array.isArray(dto.Pages)) {
      console.log('      ❌ Pages is not array:', typeof dto.Pages, dto.Pages);
      return false;
    }
    
    console.log('      ✅ Basic fields validated, checking pages...');
    const pagesValid = dto.Pages.every((page: any, index: number) => {
      if (!page) {
        console.log(`        ❌ Page ${index} is null/undefined`);
        return false;
      }
      if (typeof page.Name !== 'string') {
        console.log(`        ❌ Page ${index} Name is not string:`, typeof page.Name, page.Name);
        return false;
      }
      if (typeof page.PageNum !== 'string') {
        console.log(`        ❌ Page ${index} PageNum is not string:`, typeof page.PageNum, page.PageNum);
        return false;
      }
      if (!Array.isArray(page.Questions)) {
        console.log(`        ❌ Page ${index} Questions is not array:`, typeof page.Questions, page.Questions);
        return false;
      }
      return true;
    });
    
    if (!pagesValid) {
      console.log('      ❌ Pages validation failed');
      return false;
    }
    
    // Optional nodes array for PAS processing
    if (dto.Nodes !== undefined && !Array.isArray(dto.Nodes)) {
      console.log('      ❌ Nodes is not array when defined:', typeof dto.Nodes, dto.Nodes);
      return false;
    }
    
    console.log('      ✅ All validation checks passed');
    return true;
  }

  /**
   * Store AdminNodeDto information by domain name for developmental age calculations
   */
  private async storeAdminNodesByDomain(adminSegment: AdminSegmentDto, segmentId: string): Promise<void> {
    try {
      
      if (!adminSegment.Nodes || adminSegment.Nodes.length === 0) {
        return;
      }

      const domainNodesData: Record<string, AdminNodeDto> = {};
      
      // Process each node and store by domain name
      for (const node of adminSegment.Nodes) {
        
        // Store the complete AdminNodeDto by its name (domain identifier)
        domainNodesData[node.Name] = {
          ...node,
          Order: node.Order || [],
          Params: node.Params || [],
          Means: node.Means || [],
          Probs: node.Probs || [],
          Cuts: node.Cuts || [],
          Pages: node.Pages || []
        };
        
      }
      
      // Store the domain nodes data in local storage with segment ID for reference
      const storageKey = `admin_nodes_segment_${segmentId}`;
      await this.storage.setItem(storageKey, JSON.stringify(domainNodesData));

      // Also create a master index of available domains across all segments
      await this.updateDomainMasterIndex(Object.keys(domainNodesData), segmentId);
      
    } catch (error) {
      console.error('❌ Failed to store AdminNodeDto by domain:', error);
    }
  }

  /**
   * Update master index of available domains across all AdminSegments
   */
  private async updateDomainMasterIndex(domainNames: string[], segmentId: string): Promise<void> {
    try {
      const masterIndexKey = 'admin_nodes_domain_index';
      
      // Get existing index
      const existingIndexStr = await this.storage.getItem(masterIndexKey);
      const existingIndex: Record<string, string[]> = existingIndexStr ? JSON.parse(existingIndexStr) : {};
      
      // Update index with current segment's domains
      existingIndex[segmentId] = domainNames;
      
      // Store updated index
      await this.storage.setItem(masterIndexKey, JSON.stringify(existingIndex));
      
    } catch (error) {
      console.error('❌ Failed to update domain master index:', error);
    }
  }

  /**
   * Get AdminNodeDto for a specific domain from storage
   */
  async getAdminNodeByDomain(domainName: string, segmentId?: string): Promise<AdminNodeDto | null> {
    try {
      // If segmentId is provided, look in that specific segment
      if (segmentId) {
        const storageKey = `admin_nodes_segment_${segmentId}`;
        const nodesDataStr = await this.storage.getItem(storageKey);
        if (nodesDataStr) {
          const nodesData: Record<string, AdminNodeDto> = JSON.parse(nodesDataStr);
          return nodesData[domainName] || null;
        }
      }
      
      // Otherwise, search across all segments using the master index
      const masterIndexKey = 'admin_nodes_domain_index';
      const indexStr = await this.storage.getItem(masterIndexKey);
      if (indexStr) {
        const index: Record<string, string[]> = JSON.parse(indexStr);
        
        // Find which segment(s) contain this domain
        for (const [segId, domains] of Object.entries(index)) {
          if (domains.includes(domainName)) {
            const storageKey = `admin_nodes_segment_${segId}`;
            const nodesDataStr = await this.storage.getItem(storageKey);
            if (nodesDataStr) {
              const nodesData: Record<string, AdminNodeDto> = JSON.parse(nodesDataStr);
              const node = nodesData[domainName];
              if (node) {
                return node;
              }
            }
          }
        }
      }
      
      return null;
      
    } catch (error) {
      console.error(`❌ Failed to get AdminNodeDto for domain ${domainName}:`, error);
      return null;
    }
  }

  /**
   * Get all available domain names from stored AdminNodes
   */
  async getAvailableDomains(): Promise<string[]> {
    try {
      const masterIndexKey = 'admin_nodes_domain_index';
      const indexStr = await this.storage.getItem(masterIndexKey);
      if (indexStr) {
        const index: Record<string, string[]> = JSON.parse(indexStr);
        const allDomains = new Set<string>();
        
        // Collect all unique domain names across segments
        Object.values(index).forEach(domains => {
          domains.forEach(domain => allDomains.add(domain));
        });
        
        return Array.from(allDomains).sort();
      }
      
      return [];
      
    } catch (error) {
      console.error('❌ Failed to get available domains:', error);
      return [];
    }
  }

  /**
   * Extract milestones from already loaded AdminSegments in storage (for bundled content)
   */
  async reloadMilestonesFromLoadedSegments(
    languageId: string = '1',
    includeStandard: boolean = true,
    includePremium: boolean = false
  ): Promise<void> {
    
    try {
      // Create milestone storage instance
      const milestoneStorage = createMasterMilestoneStorage(this.storage);
      
      const allMilestones: MasterMilestone[] = [];
      const allMilestoneTexts: MilestoneText[] = [];
      let allDomainPasData: { [domain: string]: any } = {};

      if (includeStandard) {
        
        // Get AdminSegment data directly from storage
        const adminSegmentKey = 'admin_nodes_segment_11255';
        const adminNodeDataRaw = await this.storage.getItem(adminSegmentKey);
        
        if (adminNodeDataRaw) {
          
          // Parse JSON string if needed
          let adminNodeData;
          try {
            adminNodeData = typeof adminNodeDataRaw === 'string' ? JSON.parse(adminNodeDataRaw) : adminNodeDataRaw;
          } catch (parseError) {
            console.error('❌ Failed to parse AdminSegment JSON:', parseError);
            throw new Error('Invalid AdminSegment data format');
          }
          
          // Extract milestones from the loaded AdminNode data
          const domains = ['CDC_LC', 'CDC_MT', 'CDC_CG', 'CDC_SE']; // Standard domains
          
          for (const domainName of domains) {
            if (adminNodeData[domainName]) {
              const adminNode = adminNodeData[domainName];
              
              if (adminNode.Order && adminNode.Order.length > 0) {
                for (const orderItem of adminNode.Order) {
                  // Skip items with invalid mvar values
                  if (!orderItem.mvar || orderItem.mvar.trim() === '') {
                    continue;
                  }
                  
                  const milestone: MasterMilestone = {
                    mvar: orderItem.mvar,
                    domain: this.mapAdminNodeToDomain(domainName),
                    itemage: orderItem.AgeMos || 0,
                    seqnum: orderItem.Order || 0,
                    lastUpdated: new Date().toISOString(),
                    version: 1
                  };
                  
                  allMilestones.push(milestone);
                  
                  // Create milestone text if shortText is available
                  if (orderItem.shortText) {
                    const milestoneText: MilestoneText = {
                      mvar: orderItem.mvar,
                      languagetype: languageId,
                      htmltext: orderItem.shortText,
                      lastUpdated: new Date().toISOString(),
                      version: 1
                    };
                    
                    allMilestoneTexts.push(milestoneText);
                  }
                }
              }
            }
          }
          
          // Save extracted data to MASTER_DATA_KEY
          const masterData: MasterMilestoneData = {
            milestones: allMilestones,
            milestoneTexts: allMilestoneTexts,
            milestoneAnswers: [],
            milestoneAnswerTexts: [],
            concernAnswers: [],
            concernAnswerTexts: [],
            languages: [],
            lastSyncTimestamp: new Date().toISOString(),
            dataVersion: '1.0.0-bundled-extraction'
          };
          
          await milestoneStorage.saveMasterData(masterData);
          
        } else {
          console.log(`⚠️ No AdminSegment data found at key: ${adminSegmentKey}`);
          throw new Error('AdminSegment data not found in storage');
        }
      }
      
    } catch (error) {
      console.error('❌ Failed to reload milestones from loaded AdminSegments:', error);
      throw error;
    }
  }
  
  // ─── Concern extraction (AdminSegment 11272) ─────────────────────────────

  /**
   * Load AdminSegment 11272 via contentService and extract + store concern groups.
   * Mirrors the reloadStandardMilestones pattern so it can be called from
   * useMilestoneSync without requiring a pre-fetched ContentRecord.
   */
  async reloadConcerns(
    contentService: any,
    languageId: string = '1'
  ): Promise<StoredConcernData | null> {
    try {
      const dto = await this.getAdminSegment('11272', languageId, contentService);
      if (!dto) {
        console.warn('⚠️ AdminSegmentStorage: reloadConcerns — AdminSegment 11272 not found in content service');
        return null;
      }
      const result = await this.extractAndStoreConcernsFromDto(dto, languageId);
      return result;
    } catch (error) {
      console.error('❌ AdminSegmentStorage: reloadConcerns failed:', error);
      return null;
    }
  }

  /**
   * Unpack AdminSegment 11272 ContentRecord and extract + store concern groups.
   * 11272 is page-based: each page/question is a domain, each answer option is
   * one concern item (multiselect).
   */
  async extractConcernsFromSegment(
    contentRecord: ContentRecord,
    languageId: string = '1'
  ): Promise<StoredConcernData | null> {
    try {
      const dto = await this.unpackAdminSegment(contentRecord);
      if (!dto) {
        console.error('❌ Failed to unpack AdminSegment for concerns extraction');
        return null;
      }
      return await this.extractAndStoreConcernsFromDto(dto, languageId);
    } catch (error) {
      console.error('❌ extractConcernsFromSegment failed:', error);
      return null;
    }
  }

  /**
   * Walk an already-parsed AdminSegmentDto (11272) and persist StoredConcernData.
   *
   * AdminSegment 11272 is node-structured: questions live inside
   * dto.Nodes[].Pages[].Questions (NOT at dto.Pages level).
   * Each multiselect question (MvarTypeId=2) maps to one concern domain and
   * its answer options become the individual concern items.
   */
  async extractAndStoreConcernsFromDto(
    dto: AdminSegmentDto,
    languageId: string = '1'
  ): Promise<StoredConcernData> {
    const groups: StoredConcernGroup[] = [];

    // Only walk node-level pages — 11272 does NOT use top-level dto.Pages.
    const pageSources: Array<{ page: any; source: string }> = [];
    for (const node of dto.Nodes || []) {
      for (const page of node.Pages || []) {
        pageSources.push({ page, source: `node:${node.Name}` });
      }
    }

    // If no nodes/pages, log the full DTO shape to help diagnose
    if (pageSources.length === 0) {
      const allKeys = Object.keys(dto);
      const topLevelPagesArr = (dto as any).Pages ?? (dto as any).pages ?? [];
      const topLevelNodesArr = (dto as any).Nodes ?? (dto as any).nodes ?? [];
      console.warn('⚠️ extractAndStoreConcernsFromDto: 0 source pages. DTO diagnostic:');
      console.warn('   top-level keys:', allKeys);
      console.warn('   dto.Pages (PascalCase) length:', Array.isArray((dto as any).Pages) ? (dto as any).Pages.length : 'not array');
      console.warn('   dto.pages (camelCase) length:',  Array.isArray((dto as any).pages) ? (dto as any).pages.length : 'not array');
      console.warn('   dto.Nodes (PascalCase) length:', Array.isArray((dto as any).Nodes) ? (dto as any).Nodes.length : 'not array');
      console.warn('   dto.nodes (camelCase) length:',  Array.isArray((dto as any).nodes) ? (dto as any).nodes.length : 'not array');
      if (topLevelPagesArr.length > 0) {
        console.warn('   First top-level Page keys:', Object.keys(topLevelPagesArr[0]));
        const firstPageQs = topLevelPagesArr[0].Questions ?? topLevelPagesArr[0].questions ?? [];
        console.warn('   First Page Questions length:', firstPageQs.length);
        if (firstPageQs.length > 0) {
          const q = firstPageQs[0]?.Question ?? firstPageQs[0]?.question ?? firstPageQs[0];
          console.warn('   First Question keys:', q ? Object.keys(q) : 'none');
          console.warn('   First Question MvarName:', q?.MvarName ?? q?.mvarName ?? '?');
          console.warn('   First Question MvarTypeId:', q?.MvarTypeId ?? q?.mvarTypeId ?? '?');
        }
      }
      if (topLevelNodesArr.length > 0) {
        console.warn('   First Node keys:', Object.keys(topLevelNodesArr[0]));
        const firstNodePages = topLevelNodesArr[0].Pages ?? topLevelNodesArr[0].pages ?? [];
        console.warn('   First Node Pages length:', firstNodePages.length);
      }
      // Also check for any array-valued keys that might contain the questions
      for (const key of allKeys) {
        const val = (dto as any)[key];
        if (Array.isArray(val) && val.length > 0 && !['Pages','Nodes','pages','nodes'].includes(key)) {
          console.warn(`   Non-standard array key "${key}": length ${val.length}, first item keys: ${typeof val[0] === 'object' && val[0] ? Object.keys(val[0]).join(',') : typeof val[0]}`);
        }
      }
    }

    for (const { page, source } of pageSources) {
      for (const qList of page.Questions || []) {
        const question = qList.Question;
        if (!question) continue;

        // MvarTypeId is always a string in the DTO type but accept number just in case.
        const typeId = question.MvarTypeId ?? question.mvarTypeId ?? (question as any).mvarTypeID;
        if (typeId !== '2' && typeId !== 2) {
          continue;
        }

        // MvarName may be PascalCase or camelCase in the JSON
        const mvarName: string = question.MvarName ?? question.mvarName ?? '';

        // Prefer the requested language; fall back to first available.
        // HtmlQuestion field is PascalCase per the DTO type.
        const htmlQuestionArr: any[] = question.HtmlQuestion ?? question.htmlQuestion ?? [];
        const htmlQuestion: any =
          htmlQuestionArr.find((hq: any) => hq.languageTypeId === languageId || hq.LanguageTypeId === languageId) ??
          htmlQuestionArr[0];

        if (!htmlQuestion) {
          continue;
        }

        // htmlAnswer may be camelCase or PascalCase — check both.
        const answerArr: any[] = htmlQuestion.htmlAnswer ?? htmlQuestion.HtmlAnswer ?? [];

        if (answerArr.length === 0) {
          // Log the raw htmlQuestion structure so we can see the actual field names.
          continue;
        }

        const mvarNameLower = mvarName.toLowerCase();
        const items: StoredConcernItem[] = answerArr.map((answer: any) => {
          // Accept both camelCase and PascalCase field variants from the backend
          const keyValue  = answer.keyValue  ?? answer.KeyValue  ?? '';
          const dataValue = answer.dataValue ?? answer.DataValue ?? '';
          const text      = answer.aText     ?? answer.AText     ?? answer.text ?? '';
          // Full compound mvarName matching AppProfileDto MVarDto.Name: e.g. "cn_adhd_3"
          const itemMvarName = keyValue ? `${mvarNameLower}_${keyValue}` : mvarNameLower;
          return { keyValue, dataValue, text, mvarName: itemMvarName };
        });

        const domainKey = deriveConcernDomain(mvarName);
        const group: StoredConcernGroup = {
          mvarName,
          pageNum: page.PageNum ?? page.pageNum ?? '',
          questionText: htmlQuestion.qText ?? htmlQuestion.QText ?? '',
          domainKey,
          items,
        };
        groups.push(group);
      }
    }

    const concernData: StoredConcernData = {
      segmentId: dto.SurveybId,
      segmentTitle: dto.Title,
      extractedAt: new Date().toISOString(),
      languageId,
      groups,
    };

    await this.storage.setItem(CONCERN_STORAGE_KEY, JSON.stringify(concernData));
    return concernData;
  }

  /**
   * Load previously extracted StoredConcernData from storage.
   */
  async loadStoredConcernData(): Promise<StoredConcernData | null> {
    try {
      const raw = await this.storage.getItem(CONCERN_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as StoredConcernData;
    } catch {
      return null;
    }
  }

  /**
   * Helper to map AdminNode domain names to app domain names
   */
  private mapAdminNodeToDomain(adminNodeName: string): string {
    const mapping: { [key: string]: string } = {
      'CDC_LC': 'communication',
      'CDC_MT': 'motor',
      'CDC_CG': 'cognitive', 
      'CDC_SE': 'socialEmotional'
    };
    
    return mapping[adminNodeName] || adminNodeName.toLowerCase();
  }
}